/**
 * Title screen (plan §6 M2-18): Continue (the newest valid save), New career (the runtime asks for a confirm when
 * saves exist), Load (the slot list; corrupt slots named with their error and the newest valid autosave offered
 * instead), Import, and the deferred-asset progress. The first click here is the game's first gesture.
 */
import type { ReactNode } from 'react'
import { FocusButton } from '../focus'
import { G, LOAD_COLS, TITLE_ITEMS } from '../models/layout'
import type { TitleView } from '../models/layout'
import type { TitleModel } from '../models/saves'
import type { ScreenProps } from './common'

export function TitleScreen(p: ScreenProps & { model: TitleModel; view: TitleView; setView: (v: TitleView) => void }): ReactNode {
  const { model, ctl } = p
  const fallback = model.fallback
  return (
    <div className="ui-title">
      <div className="ui-title__brand">
        <p className="ui-title__kicker">A Charter Reach story</p>
        <h1 className="ui-title__name">Umbra Reach</h1>
        <p className="ui-title__tag">Mine. Salvage. Pay off the Kestrel.</p>
      </div>
      <div className="ui-panel ui-title__panel">
        {model.storageBanner !== null ? <p className="ui-banner" role="status">{'⚠'} {model.storageBanner}</p> : null}
        {p.view === 'main' ? (
          <div className="ui-menu">
            <FocusButton group={G.title} index={TITLE_ITEMS.indexOf('continue')} testId="title-continue" className="ui-menu__item" disabled={model.continueSlot === null} onActivate={() => ctl.runAsync(() => ctl.actions.continueCareer())}>
              <span className="ui-menu__label">{model.continueLabel}</span>
              <span className="ui-menu__hint">{model.continueDetail}</span>
            </FocusButton>
            <FocusButton group={G.title} index={TITLE_ITEMS.indexOf('new')} testId="title-new" className="ui-menu__item" onActivate={() => ctl.runAsync(() => ctl.actions.newCareer())}>
              <span className="ui-menu__label">New career</span>
              <span className="ui-menu__hint">{model.hasSaves ? 'Your saves stay in their slots' : 'Lease a Kestrel from Tethys Crew Finance'}</span>
            </FocusButton>
            <FocusButton group={G.title} index={TITLE_ITEMS.indexOf('load')} testId="title-load" className="ui-menu__item" disabled={!model.hasSaves} onActivate={() => p.setView('load')}>
              <span className="ui-menu__label">Load</span>
              <span className="ui-menu__hint">{model.anyBad ? '⚠ A save needs attention' : 'Manual saves and autosaves'}</span>
            </FocusButton>
            <FocusButton group={G.title} index={TITLE_ITEMS.indexOf('import')} testId="title-import" className="ui-menu__item" onActivate={() => ctl.importFile()}>
              <span className="ui-menu__label">Import</span>
              <span className="ui-menu__hint">Load a save file from this computer</span>
            </FocusButton>
          </div>
        ) : (
          <div className="ui-load">
            <h2 className="ui-panel__title">Load</h2>
            {model.rows.map((row, i) => (
              <div key={row.slot} className="ui-row ui-row--save" data-status={row.status}>
                <span className="ui-row__main">
                  <span className="ui-row__title">
                    {row.label}
                    {row.error !== null ? <span className="ui-tag" data-kind="error">{'⚠'} {row.status === 'future' ? 'Newer version' : 'Corrupt'}</span> : null}
                  </span>
                  <span className="ui-row__sub">
                    {row.summary}
                    {row.savedAt !== '' ? ` · saved ${row.savedAt}` : ''}
                  </span>
                  {row.error !== null ? <span className="ui-row__sub ui-error-text">{row.error}</span> : null}
                </span>
                <FocusButton group={G.load} index={i * LOAD_COLS} testId={`title-load-${row.slot}`} className="ui-btn--small" disabled={!row.canLoad} onActivate={() => ctl.runAsync(() => ctl.actions.loadSlot(row.slot))}>
                  Load
                </FocusButton>
                <FocusButton group={G.load} index={i * LOAD_COLS + 1} testId={`title-export-${row.slot}`} className="ui-btn--small ui-btn--quiet" disabled={!row.canExport} onActivate={() => ctl.exportSlot(row.slot)}>
                  Export
                </FocusButton>
              </div>
            ))}
            {model.anyBad && fallback !== null ? (
              <FocusButton group={G.fallback} index={0} testId="title-load-fallback" className="ui-btn--wide" onActivate={() => ctl.runAsync(() => ctl.actions.loadSlot(fallback.slot))}>
                Load the newest valid autosave instead ({fallback.label})
              </FocusButton>
            ) : null}
            <FocusButton group={G.loadBack} index={0} testId="title-back" className="ui-btn--wide ui-btn--quiet" onActivate={() => p.setView('main')}>
              Back
            </FocusButton>
          </div>
        )}
        <p className="ui-title__assets" data-ready={model.assetsReady ? '1' : '0'}>
          {model.assetsLabel}
        </p>
      </div>
    </div>
  )
}
